import React, { useState } from "react";
import { 
  FiAlertCircle, 
  FiClock, 
  FiXCircle, 
  FiRotateCcw, 
  FiCheckCircle, 
  FiHelpCircle
} from "react-icons/fi";

export type RcaHoldCancelReopenProps = {
  rcaCode?: string;
  currentStatus?: string;
  onExecuteAction?: (actionType: "HOLD" | "CANCEL" | "REOPEN", data: Record<string, unknown>) => void;
  onClose?: () => void;
};

export function RcaHoldCancelReopen({
  rcaCode = "RCA-2026-0001",
  currentStatus = "In Progress",
  onExecuteAction,
  onClose,
}: RcaHoldCancelReopenProps) {
  const [activeTab, setActiveTab] = useState<"HOLD" | "CANCEL" | "REOPEN">("HOLD");
  const [reasonCategory, setReasonCategory] = useState<string>("WAITING_CUSTOMER_DATA");
  const [reasonId, setReasonId] = useState<string>("RSN-CUST-01");
  const [description, setDescription] = useState<string>("Awaiting CMM inspection data and returned physical parts from customer facility.");
  const [expectedResumeDate, setExpectedResumeDate] = useState<string>("2026-09-02");
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSuccess(true);
    onExecuteAction?.(activeTab, {
      rcaCode,
      reasonCategory,
      reasonId,
      description,
      expectedResumeDate,
    });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-4 p-6">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; Lifecycle Control</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">10. Hold / Cancel / Reopen</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Execute controlled lifecycle state transitions subject to separation-of-duties and audit rules.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-medium">
          <button
            type="button"
            onClick={() => setActiveTab("HOLD")}
            className={`px-4 py-1.5 rounded-md font-semibold transition-all ${
              activeTab === "HOLD" 
                ? "bg-white text-amber-700 shadow-sm font-bold" 
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Hold
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("CANCEL")}
            className={`px-4 py-1.5 rounded-md font-semibold transition-all ${
              activeTab === "CANCEL" 
                ? "bg-white text-rose-700 shadow-sm font-bold" 
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("REOPEN")}
            className={`px-4 py-1.5 rounded-md font-semibold transition-all ${
              activeTab === "REOPEN" 
                ? "bg-white text-blue-700 shadow-sm font-bold" 
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Reopen
          </button>
        </div>
      </div>

      {/* Target Record Summary */}
      <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div>
          <span className="text-slate-500">Record: </span>
          <strong className="text-blue-600 font-bold">{rcaCode}</strong>
        </div>
        <div>
          <span className="text-slate-500">Current Status: </span>
          <span className="px-2.5 py-0.5 rounded-full font-bold text-[11px] bg-blue-50 text-blue-700 border border-blue-200">
            {currentStatus}
          </span>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-slate-700 font-semibold mb-1">Reason Category:</label>
            <select
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
              className="w-full p-2.5 rounded-lg border border-slate-300 bg-white font-medium text-slate-800"
            >
              <option value="WAITING_CUSTOMER_DATA">Waiting for Customer Data / Samples</option>
              <option value="SUPPLIER_INVESTIGATION">Pending Supplier Containment Report</option>
              <option value="LAB_TESTING">Awaiting External Laboratory Metallurgy Test</option>
              <option value="DUPLICATE">Duplicate RCA Record</option>
              <option value="INVALID_DEFECT">Invalid Defect / Customer Retracted</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">
              {activeTab === "HOLD" ? "Expected Resume Date:" : "Authorization Reference ID:"}
            </label>
            <input
              type={activeTab === "HOLD" ? "date" : "text"}
              value={expectedResumeDate}
              onChange={(e) => setExpectedResumeDate(e.target.value)}
              className="w-full p-2.5 rounded-lg border border-slate-300 bg-white text-slate-800 font-semibold"
            />
          </div>
        </div>

        <div>
          <label className="block text-slate-700 font-semibold mb-1">Detailed Explanation & Governance Justification:</label>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-500 font-medium"
            placeholder="Document rationale, customer authorization emails, or QA sign-off notes..."
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-semibold"
            >
              Cancel
            </button>
          )}

          <button
            type="submit"
            className={`px-5 py-2.5 text-white font-semibold rounded-lg shadow-sm flex items-center gap-2 ${
              activeTab === "HOLD" 
                ? "bg-amber-600 hover:bg-amber-700" 
                : activeTab === "CANCEL" 
                ? "bg-rose-600 hover:bg-rose-700" 
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {activeTab === "HOLD" && <FiClock className="w-4 h-4" />}
            {activeTab === "CANCEL" && <FiXCircle className="w-4 h-4" />}
            {activeTab === "REOPEN" && <FiRotateCcw className="w-4 h-4" />}
            Execute {activeTab === "HOLD" ? "Hold Transition" : activeTab === "CANCEL" ? "Cancellation" : "Reopen Workflow"}
          </button>
        </div>
      </form>

      {isSuccess && (
        <div className="p-3.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg text-xs flex items-center gap-2">
          <FiCheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>Status transition executed and written to immutable audit log.</span>
        </div>
      )}
    </div>
  );
}
